// CategoriesView.jsx — proc-v3 (F2.3h): DECIMA VISTA DEL MONOLITE in React,
// "Categorie" (albero tassonomia), flag-gated (shell_proc_v3_categories,
// default OFF). PRIMA vista sul TERZO componente condiviso del design system
// (TreeView: stati obbligatori + walk depth-first in tree-view-core).
//
// Replica 1:1 renderCategories di proc-v0.js:8092-8963: header (titolo dict,
// sottotitolo/bottoni hardcoded IT — CAT_TEXTS VERBATIM in api.js), pannello
// albero (ricerca token-AND su path+name+keywords con highlight, vista
// Albero/Lista, ⊞/⊟, righe nodo con badge ◆N e conteggio fornitori, azioni
// hover, righe prodotto sotto le foglie, riga "⋯ altre N categorie" in
// ricerca, menu contestuale con Taglia/Incolla) + pannello dettaglio a 4 tab
// (suppliers/products/quotations/settings coi keyword AI). Le righe usano le
// classi cat-* GLOBALI di index.html (stesso CSS del v1 ⇒ parità pixel).
//
// STATO DI VISTA (parità v1): catSelected/catTab/catSearch/catSearchPrev/
// catExpanded/catViewMode sono binding LESSICALI (v1:8059-8078) → closure
// della factory (viewState); i TRE Set di ricerca e la clipboard sono GLOBALI
// su window nel v1 → si usano GLI STESSI (interop v1↔v3).
//
// CONFINE DICHIARATO (mandato f23h): drag&drop SOLO nel v1 — lo spostamento
// resta disponibile via Taglia/Incolla (stessa primitiva
// confirmWrite.moveCategory ⇒ payload identico). Le righe v3 NON portano
// draggable/title di trascinamento (mai promettere un gesto che qui non c'è).
//
// CONFINI (pattern f23c): modali prodotto/fornitore/preventivo/AI = V1
// (deleghe in api.js); MAI 'nav:badge' da questo modulo.

import React from 'react';
import { TreeView } from '../ui/TreeView.jsx';
import { indentPx } from '../ui/tree-view-core.js';
import { splitByTokens } from '../ui/data-table-core.js';
import {
  CAT_TEXTS, CAT_SENTINEL, catLabel,
  catTreeContext, catNodeProducts, catNodeCounts, catProdBadgeTitle,
  catMoreLabel, catToggleNode, catEnsureExpanded, catApplySearchInput,
  catRenderPreamble,
  catListEntries, catListHeaderParts, catListCategoryCounts,
  catDetailSuppliers, catDetailQuotations, catTabLabel,
  catTagBadgeParts, catConfidenceParts,
  catAdd, catRename, catDelete, catPaste, catCut,
  catCtxExpandSub, catCtxCollapseSub, catCtxOnlyHere,
  catAddTagToSupplier, catAssignSupplier, catQuickCreateSupplier,
  catKeywordManual, catKeywordGenerate,
  catOpenProductDetail, catOpenSupplierDetail, catOpenProductModal,
  catOpenSupplierModal, catOpenQuotationModal,
  catHeaderAiAssistant, catHeaderAiImport, catAiCategorize,
} from './api.js';

// Evidenziazione dei token (v1:8320-8330): STESSO <mark> del v1; lo split è
// splitByTokens del design system (token più lunghi prima) — textContent
// identico al v1, deviazione di sola forma dichiarata (il v1 applica i token
// in sequenza sull'HTML già marcato).
function Highlight({ text, tokens }) {
  const s = String(text == null ? '' : text);
  if (!tokens || !tokens.length) return s;
  return splitByTokens(s, tokens).map((seg, i) => (
    seg.hit
      ? <mark key={i} className="bg-yellow-200 text-yellow-900 px-0.5 rounded">{seg.text}</mark>
      : <React.Fragment key={i}>{seg.text}</React.Fragment>
  ));
}

// Badge tag del v1 (tagBadge v1:3152) — stessi colori/classi, resa React.
function TagBadge({ tag }) {
  const p = catTagBadgeParts(tag);
  return (
    <span className={p.color + ' text-[10px] px-1.5 py-0.5 rounded inline-block'}>
      {p.body}
      {p.scope ? <>{' '}<span className="opacity-60 font-normal">{'@ ' + p.scope}</span></> : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// RIGA NODO + righe prodotto (parità _renderCatTree/renderNode v1:8332-8373)
// ---------------------------------------------------------------------------
function CatRow({ win, entry, ctx, viewState, on }) {
  const n = entry.node;
  const depth = entry.depth;
  const hasKids = entry.hasChildren;
  const isOpen = ctx.effectiveExpanded.has(n.path); // per foglie: prodotti visibili se "aperte" (glifo ·)
  const isSel = n.path === viewState.selected;
  const isHit = ctx.tokens.length > 0 && ctx.matches.has(n.path);
  const { supCount, subtreeProductCount } = catNodeCounts(win, n.path);
  const name = win.prettyLabel(n.path.split('/').pop());
  const products = catNodeProducts(win, n, {
    isOpen, isSelected: isSel, tokens: ctx.tokens, matches: ctx.matches,
  });

  const rowClick = () => {
    viewState.selected = n.path;
    const categories = (win.DB && win.DB.categories) || [];
    const hasChildren = categories.some((c) => c.path.startsWith(n.path + '/'));
    if (hasChildren) on.toggle(n.path);
    on.repaintBoth();
  };
  const toggleClick = (e) => {
    e.stopPropagation();
    on.toggle(n.path);
    viewState.selected = n.path;
    on.repaintBoth();
  };

  return (
    <>
      <div
        className={'cat-row' + (isSel ? ' selected' : '') + (isHit ? ' cat-hit' : '')}
        data-path={n.path}
        style={{ paddingLeft: indentPx(depth) + 'px' }}
        onClick={rowClick}
        onContextMenu={(e) => { e.preventDefault(); on.openCtxMenu(e.pageX, e.pageY, n.path); }}
      >
        <span className="cat-toggle" onClick={toggleClick}>{hasKids ? (isOpen ? '▾' : '▸') : '·'}</span>
        <span className={'cat-name cat-d' + Math.min(depth, 3)}><Highlight text={name} tokens={ctx.tokens} /></span>
        {subtreeProductCount > 0
          ? <span className="cat-prod-badge" title={catProdBadgeTitle(subtreeProductCount)}>{'◆' + subtreeProductCount}</span>
          : null}
        <span className="cat-count">{supCount}</span>
        <span className="cat-actions">
          <button type="button" title={CAT_TEXTS.addSubTitle} onClick={(e) => { e.stopPropagation(); on.add(n.path); }}>+</button>
          <button type="button" title={CAT_TEXTS.addProdTitle} onClick={(e) => { e.stopPropagation(); catOpenProductModal(win, null, n.path); }}>◆+</button>
          <button type="button" title={CAT_TEXTS.renameTitle} onClick={(e) => { e.stopPropagation(); on.rename(n.path); }}>⌇</button>
          <button type="button" title={CAT_TEXTS.deleteTitle} onClick={(e) => { e.stopPropagation(); on.del(n.path); }}>×</button>
        </span>
      </div>
      {products.map((p) => (
        <div
          key={p.id}
          className="cat-product-row"
          data-product-id={p.id}
          style={{ paddingLeft: indentPx(depth + 1) + 'px' }}
          onClick={(e) => { e.stopPropagation(); catOpenProductDetail(win, p.id); }}
        >
          <span className="cat-product-bullet">◆</span>
          <span className="cat-product-name"><Highlight text={p.name} tokens={ctx.tokens} /></span>
          {p.brand ? <span className="cat-product-brand">{p.brand}</span> : null}
        </div>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// MENU CONTESTUALE (parità _showCatContextMenu v1:8526-8589) — PORTAL su
// document.body con position:absolute a pageX/pageY, ESATTAMENTE come il v1
// (menu.appendChild su body): con la pagina scrollata il menu resta
// raggiungibile scorrendo (con fixed non lo sarebbe — trovato dal collaudo).
// Chiusura al primo click ovunque (document).
// ---------------------------------------------------------------------------
function CatContextMenu({ win, menu, on }) {
  React.useEffect(() => {
    const doc = win.document;
    const close = () => on.closeCtxMenu();
    // v1 aggancia il close con setTimeout(0): qui l'effect gira già DOPO il
    // contextmenu (e il click destro non genera click) — aggancio diretto.
    doc.addEventListener('click', close);
    return () => doc.removeEventListener('click', close);
  }, [win, on]);
  const path = menu.path;
  const clipboard = win._catClipboard;
  const itemCls = 'block w-full text-left px-3 py-1.5 hover:bg-slate-100';
  return (
    <div
      id="cat-ctx-menu"
      className="absolute bg-white border border-slate-200 rounded shadow-lg text-sm z-50 py-1"
      style={{ left: menu.x + 'px', top: menu.y + 'px' }}
    >
      <button type="button" className={itemCls} onClick={() => { on.ctxExpandSub(path); on.closeCtxMenu(); }}>{CAT_TEXTS.ctxExpandSub}</button>
      <button type="button" className={itemCls} onClick={() => { on.ctxCollapseSub(path); on.closeCtxMenu(); }}>{CAT_TEXTS.ctxCollapseSub}</button>
      <button type="button" className={itemCls} onClick={() => { on.ctxOnlyHere(path); on.closeCtxMenu(); }}>{CAT_TEXTS.ctxOnlyHere}</button>
      <hr className="my-1" />
      <button type="button" className={itemCls} onClick={() => { on.add(path); on.closeCtxMenu(); }}>{CAT_TEXTS.ctxAdd}</button>
      <button type="button" className={itemCls} onClick={() => { on.rename(path); on.closeCtxMenu(); }}>{CAT_TEXTS.ctxRename}</button>
      <button type="button" className="block w-full text-left px-3 py-1.5 hover:bg-rose-50 text-rose-700" onClick={() => { on.del(path); on.closeCtxMenu(); }}>{CAT_TEXTS.ctxDelete}</button>
      <hr className="my-1" />
      <button type="button" className={itemCls} onClick={() => { on.cut(path); on.closeCtxMenu(); }}>{CAT_TEXTS.ctxCut}</button>
      <button type="button" className={itemCls + (clipboard ? '' : ' opacity-40')} onClick={() => { on.paste(path); on.closeCtxMenu(); }}>
        {CAT_TEXTS.ctxPaste}{clipboard ? ' — "' + clipboard.split('/').pop() + '"' : ''}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VISTA LISTA (parità _renderCatList v1:8180-8255)
// ---------------------------------------------------------------------------
function CatList({ win, viewState, on }) {
  const { tokens, entries } = catListEntries(win, viewState.search);
  if (!entries.length) {
    return <div className="text-sm text-slate-400 italic p-3">{CAT_TEXTS.emptyList}</div>;
  }
  const header = catListHeaderParts(entries.length, viewState.search, tokens);
  return (
    <>
      <div className="text-xs text-slate-500 mb-2 px-1">
        {header.countText}
        {header.query != null ? <>{' per '}<strong>{'"' + header.query + '"'}</strong></> : null}
      </div>
      {entries.map((e, i) => {
        if (e.kind === 'category') {
          const parts = e.path.split('/');
          const crumbs = parts.slice(0, -1);
          const leaf = parts[parts.length - 1];
          const isSel = e.path === viewState.selected;
          const counts = catListCategoryCounts(win, e.path);
          return (
            <div
              key={'c-' + e.path}
              className={'cat-list-row' + (isSel ? ' selected' : '')}
              data-list-cat={e.path}
              onClick={() => { viewState.selected = e.path; on.repaintBoth(); }}
            >
              {crumbs.length ? (
                <div className="cat-list-crumb">
                  {crumbs.map((p, j) => (
                    <React.Fragment key={j}>
                      {j ? <span className="cat-list-crumb-sep">/</span> : null}
                      <Highlight text={p} tokens={tokens} />
                    </React.Fragment>
                  ))}
                </div>
              ) : null}
              <div className="cat-list-crumb-leaf"><Highlight text={leaf} tokens={tokens} /></div>
              <div className="text-xs text-slate-500 mt-1">{counts.text}</div>
            </div>
          );
        }
        const parts = e.path.split('/');
        return (
          <div
            key={'p-' + e.productId}
            className="cat-list-row"
            data-list-prod={e.productId}
            style={{ borderLeft: '3px solid rgb(8 145 178)' }}
            onClick={() => catOpenProductDetail(win, e.productId)}
          >
            <div className="cat-list-crumb">
              {parts.map((p, j) => (
                <React.Fragment key={j}>
                  {j ? <span className="cat-list-crumb-sep">/</span> : null}
                  <Highlight text={p} tokens={tokens} />
                </React.Fragment>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-cyan-700 text-xs">{CAT_TEXTS.productMarker}</span>
              <div className="cat-list-crumb-leaf"><Highlight text={e.name} tokens={tokens} /></div>
            </div>
            {e.brand ? <div className="text-xs text-slate-500 mt-1"><Highlight text={e.brand} tokens={tokens} /></div> : null}
          </div>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// PANNELLO DETTAGLIO (parità _renderCatDetail v1:8658-8963)
// ---------------------------------------------------------------------------

function DetailBox({ children }) {
  return <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 text-center text-slate-400 italic">{children}</div>;
}

function SuppliersTab({ win, viewState, suppliers, detailRev, on }) {
  const selected = viewState.selected;
  const pickRef = React.useRef(null);
  // Picker makeSearchableSelect DEL MONOLITE (delega): ricreato quando cambia
  // selezione o dopo una scrittura del dettaglio (detailRev) — MAI sui repaint
  // del solo albero (il testo digitato nel picker non si perde, regole UI 1).
  React.useEffect(() => {
    const div = pickRef.current;
    if (!div) return undefined;
    const directIds = new Set((suppliers || []).filter((s) => (s.categories || []).includes(selected)).map((s) => s.id));
    const opts = win.store.listSuppliers({ excludeKind: 'competitor' })
      .filter((s) => !directIds.has(s.id))
      .map((s) => ({ value: s.id, label: s.name, sub: [s.country || '', (s.categories || []).length ? (s.categories.length + ' cat') : ''].filter(Boolean).join(' · ') }));
    const pick = win.makeSearchableSelect({
      options: opts,
      placeholder: CAT_TEXTS.supPickPlaceholder,
      onCreate: (q) => catQuickCreateSupplier(win, q, { categories: [selected] }),
      onChange: async (supId) => {
        const ok = await catAssignSupplier(win, supId, selected);
        if (ok) on.repaintDetail();
      },
    });
    div.appendChild(pick.rootEl);
    return () => { div.replaceChildren(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, detailRev]);

  return (
    <>
      <div className="flex justify-between items-start mb-3 gap-2 flex-wrap">
        <p className="text-xs text-slate-500 flex-1 min-w-[160px]">{CAT_TEXTS.supDesc}</p>
        <div className="flex gap-2 items-center flex-wrap">
          <div id="cat-sup-pick" style={{ minWidth: '260px' }} ref={pickRef}></div>
          <button
            id="cat-add-supplier" type="button"
            className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded text-xs whitespace-nowrap"
            onClick={() => catOpenSupplierModal(win, selected)}
          >{CAT_TEXTS.supNewBtn}</button>
        </div>
      </div>
      {suppliers.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: '540px' }}>
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr><th className="text-left px-2 py-2">Fornitore</th><th className="text-left px-2 py-2">Paese</th><th className="text-left px-2 py-2">Stato</th><th className="text-left px-2 py-2">Affid.</th><th className="text-left px-2 py-2">Diretto?</th><th className="text-left px-2 py-2">Tag</th></tr>
            </thead>
            <tbody>
              {suppliers.map((s) => {
                const direct = (s.categories || []).includes(selected);
                const tags = (s.tags || []);
                const conf = catConfidenceParts(win, s.confidence);
                return (
                  <tr key={s.id} className="border-t hover:bg-slate-50 row-link sup-open" onClick={() => catOpenSupplierDetail(win, s.id)}>
                    <td className="px-2 py-2 font-medium">{s.name}</td>
                    <td className="px-2 py-2 text-xs">{s.country || ''}</td>
                    <td className="px-2 py-2">{s.qualified
                      ? <span className="bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 rounded">qualified</span>
                      : <span className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded">prospect</span>}</td>
                    <td className="px-2 py-2 text-xs"><span className={conf.color + ' text-xs font-medium'}>{conf.label}</span></td>
                    <td className="px-2 py-2 text-xs">{direct
                      ? <span className="text-emerald-700">{CAT_TEXTS.supDirect}</span>
                      : <span className="text-slate-400">{CAT_TEXTS.supViaDesc}</span>}</td>
                    <td className="px-2 py-2 text-xs whitespace-nowrap">
                      {tags.slice(0, 3).map((tg, i) => <React.Fragment key={tg}>{i ? ' ' : null}<TagBadge tag={tg} /></React.Fragment>)}
                      {tags.length > 3 ? <span className="text-[10px] text-slate-400 ml-1">{'+' + (tags.length - 3)}</span> : null}
                      {' '}
                      <button
                        type="button" className="cat-sup-tag text-cyan-700 hover:underline ml-1"
                        onClick={async (e) => { e.stopPropagation(); const ok = await catAddTagToSupplier(win, s.id); if (ok) on.repaintDetail(); }}
                      >{CAT_TEXTS.supAddTag}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <div className="text-sm text-slate-400 italic py-3">{CAT_TEXTS.supEmpty}</div>}
    </>
  );
}

function ProductsTab({ win, viewState, products, isLeaf }) {
  const selected = viewState.selected;
  return (
    <>
      <div className="flex justify-between items-center mb-3 gap-2 flex-wrap">
        <p className="text-xs text-slate-500">{'Prodotti in questo ramo' + (isLeaf ? '' : ' (tra tutti i discendenti)') + '.'}</p>
        <div className="flex gap-2">
          {selected === CAT_SENTINEL && products.length ? (
            <button
              id="cat-ai-categorize" type="button"
              className="text-white px-3 py-1.5 rounded text-xs"
              style={{ background: 'linear-gradient(135deg,#7c3aed,#2563eb)' }}
              onClick={() => catAiCategorize(win, products)}
            >{CAT_TEXTS.prodAiBtn}</button>
          ) : null}
          <button
            id="cat-add-product" type="button"
            className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded text-xs"
            onClick={() => {
              if (!isLeaf) {
                if (!win.confirm(`"${selected}" è un ramo (ha sottocategorie). I prodotti risiedono nelle foglie. Scegli una foglia oppure continua e il prodotto verrà creato qui comunque.`)) return;
              }
              catOpenProductModal(win, null, selected);
            }}
          >{CAT_TEXTS.prodAddBtn}</button>
        </div>
      </div>
      {products.length ? (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left px-2 py-2">Nome</th><th className="text-left px-2 py-2">Categoria</th><th className="text-left px-2 py-2">Brand</th><th className="text-left px-2 py-2">Preventivi</th></tr></thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t hover:bg-slate-50 row-link prod-open" onClick={() => catOpenProductDetail(win, p.id)}>
                <td className="px-2 py-2 font-medium">{p.name}</td>
                <td className="px-2 py-2 text-xs"><code className="bg-slate-100 px-1 rounded">{p.category}</code></td>
                <td className="px-2 py-2 text-xs">{p.brand || '—'}</td>
                <td className="px-2 py-2 text-xs">{win.store.listQuotationsByProduct(p.id).length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div className="text-sm text-slate-400 italic py-3">{CAT_TEXTS.prodEmpty}</div>}
    </>
  );
}

function QuotationsTab({ win, products, quotations }) {
  if (!quotations.length) return <div className="text-sm text-slate-400 italic py-3">{CAT_TEXTS.quotEmpty}</div>;
  return (
    <>
      <div className="bg-cyan-50 border border-cyan-200 rounded p-2 text-xs text-cyan-900 mb-3">{quotations.length + ' preventivi attivi su ' + products.length + ' prodotto/i in questo ramo.'}</div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left px-2 py-2">Prodotto</th><th className="text-left px-2 py-2">Fornitore</th><th className="text-left px-2 py-2">Magazzino</th><th className="text-left px-2 py-2">MOQ</th><th className="text-left px-2 py-2">Prezzo</th><th className="text-left px-2 py-2">Consegna</th><th className="text-left px-2 py-2">Incoterm</th><th className="text-left px-2 py-2">All.</th></tr></thead>
        <tbody>
          {quotations.map((q) => {
            const sup = win.store.getSupplier(q.supplierId);
            const attCount = (q.attachments || []).length;
            const hasNotes = !!(q.notes && q.notes.trim());
            return (
              <tr key={q.id} className="border-t hover:bg-cyan-50 quote-row cursor-pointer" onClick={() => catOpenQuotationModal(win, q.id)}>
                <td className="px-2 py-2">
                  <a
                    href="#" className="prod-open text-cyan-700 hover:underline"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); catOpenProductDetail(win, q._product.id); }}
                  >{q._product.name}</a>
                </td>
                <td className="px-2 py-2 text-xs">
                  {sup ? sup.name : '?'}
                  {q.isMarketRef ? <>{' '}<span className="bg-fuchsia-100 text-fuchsia-800 text-[10px] px-1 rounded">market ref</span></> : null}
                </td>
                <td className="px-2 py-2 text-xs">{q.warehouse}</td>
                <td className="px-2 py-2 text-xs">{q.moq}</td>
                <td className="px-2 py-2 font-medium">{q.currency + ' ' + q.price + (q.priceUnit || '')}</td>
                <td className="px-2 py-2 text-xs">{q.leadTimeWeeks + 'w'}</td>
                <td className="px-2 py-2 text-xs">{q.incoterm || ''}</td>
                <td className="px-2 py-2 text-xs whitespace-nowrap">
                  {attCount ? <span className="bg-cyan-100 text-cyan-800 px-1.5 rounded text-[10px]">{'All. ' + attCount}</span> : null}
                  {hasNotes ? <span className="bg-amber-100 text-amber-800 px-1.5 rounded text-[10px] ml-1"></span> : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function SettingsTab({ win, viewState, selObj, isLeaf, on }) {
  const selected = viewState.selected;
  const kws = (selObj.keywords || []);
  const [manualKw, setManualKw] = React.useState('');
  const [manualBusy, setManualBusy] = React.useState(false);
  const [gen, setGen] = React.useState({ busy: false, status: '', retry: false, errore: false, bloccoOra: '' });
  // limite d'uso: pulsante spento fino all'ora indicata, poi di nuovo attivo da solo
  React.useEffect(() => {
    if (!gen.bloccoFino) return undefined;
    const ms = gen.bloccoFino - Date.now();
    const t = win.setTimeout(() => setGen((g) => ({ ...g, bloccoFino: 0, bloccoOra: '' })), Math.max(0, Math.min(ms, 6 * 3600 * 1000)));
    return () => win.clearTimeout(t);
  }, [gen.bloccoFino]);

  // add keyword manuale (v1:8886-8912): stessi toast, input svuotato su ok
  const doAddManualKw = async () => {
    const v = (manualKw || '').trim();
    if (!v) return;
    setManualBusy(true);
    try {
      const data = await catKeywordManual(win, selected, v, 'add');
      if (data.alreadyPresent) win.toast(`Keyword "${v}" già presente`, 'info');
      else win.toast(`Aggiunta keyword: ${v}`, 'success');
      setManualKw('');
      on.repaintDetail();
    } catch (e) {
      win.toast('Errore: ' + e.message, 'error');
    } finally { setManualBusy(false); }
  };

  // generate (v1:8935-8961): busy + status; su errore bottone 'Riprova'
  const doGenerate = async () => {
    // attesa con l'ora prevista (come la vista v0): di solito meno di un minuto
    const pronte = new Date(Date.now() + 60 * 1000).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });
    setGen({ busy: true, status: "L'AI interna sta generando le parole chiave: pronte verso le " + pronte + ' (di solito meno di un minuto).', retry: false, errore: false });
    try {
      const data = await catKeywordGenerate(win, selected);
      // esito sempre scritto sotto il pulsante: quante parole chiave nuove (o nessuna) ha aggiunto la rigenerazione
      const nuove = Number(data.nuoveAggiunte != null ? data.nuoveAggiunte : data.keywordsAdded);
      const esito = Number.isFinite(nuove)
        ? (nuove > 0 ? (nuove === 1 ? 'Aggiunta 1 parola chiave nuova.' : 'Aggiunte ' + nuove + ' parole chiave nuove.') : "Nessuna parola chiave nuova: quelle proposte dall'AI c'erano già.")
        : ((data.keywords && data.keywords.length) || 0) + ' parole chiave generate.';
      win.toast(esito, nuove === 0 ? 'info' : 'success');
      setGen({ busy: false, status: esito, retry: false, errore: false });
      on.repaintDetail();
    } catch (e) {
      const fino = e && e.codice === 'limite' && e.riprova_alle ? new Date(e.riprova_alle).getTime() : 0;
      const blocco = fino > Date.now() ? { bloccoFino: fino, bloccoOra: e.riprova_alle_ora || new Date(fino).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' }) } : { bloccoFino: 0, bloccoOra: '' };
      setGen({ busy: false, status: e.message, retry: true, errore: true, ...blocco });
      win.toast(e.message, 'error');
    }
  };

  const bloccato = !!(gen.bloccoFino && gen.bloccoFino > Date.now());
  const genLabel = gen.busy ? CAT_TEXTS.kwGenerating
    : (bloccato ? 'Riprova dopo le ' + gen.bloccoOra : (gen.retry ? CAT_TEXTS.kwRetry : (kws.length ? CAT_TEXTS.kwRegenerate : CAT_TEXTS.kwGenerate)));

  return (
    <div className="space-y-4 text-sm cat-set-pad">
      {/* telefono: margine dal bordo e spazio in fondo, cosi' il pulsante tondo fisso non copre "Aggiungi" */}
      <style>{'.cat-set-pad{padding:0 12px 104px}@media (min-width:640px){.cat-set-pad{padding:0}}'}</style>
      <div>
        <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">{CAT_TEXTS.setPath}</label>
        <code className="block bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm">{selected}</code>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">{CAT_TEXTS.setTipo}</label>
        <span className={(isLeaf ? 'bg-cyan-100 text-cyan-800' : 'bg-slate-100 text-slate-700') + ' text-xs px-2 py-0.5 rounded'}>{isLeaf ? CAT_TEXTS.setLeafLong : CAT_TEXTS.setBranchLong}</span>
      </div>
      <div className="flex gap-2 pt-2">
        <button id="cat-set-rename" type="button" className="bg-slate-200 hover:bg-slate-300 px-3 py-1.5 rounded text-sm" onClick={() => on.rename(selected)}>{CAT_TEXTS.setRename}</button>
        <button id="cat-set-add" type="button" className="bg-slate-200 hover:bg-slate-300 px-3 py-1.5 rounded text-sm" onClick={() => on.add(selected)}>{CAT_TEXTS.setAdd}</button>
        <button id="cat-set-del" type="button" className="bg-rose-100 hover:bg-rose-200 text-rose-700 px-3 py-1.5 rounded text-sm" onClick={() => on.del(selected)}>{CAT_TEXTS.setDelete}</button>
      </div>
      <div className="pt-3 border-t border-slate-200">
        <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">{CAT_TEXTS.kwLabel} <span className="text-slate-400 normal-case">{CAT_TEXTS.kwLabelSub}</span></label>
        <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-2 min-h-[2.5rem]" id="cat-ai-keywords-display">
          {kws.length ? kws.map((k) => (
            <span
              key={k}
              className="cat-kw-chip inline-block bg-violet-100 text-violet-800 hover:bg-rose-100 hover:text-rose-800 text-xs px-2 py-0.5 rounded mr-1 mb-1 cursor-pointer"
              title={CAT_TEXTS.kwChipTitle}
              onClick={async () => {
                if (!win.confirm(`Eliminare la keyword "${k}"?`)) return;
                try {
                  await catKeywordManual(win, selected, k, 'remove');
                  win.toast(`Eliminata: ${k}`, 'success');
                  on.repaintDetail();
                } catch (e) { win.toast('Errore: ' + e.message, 'error'); }
              }}
            >{k + ' ×'}</span>
          )) : <span className="text-xs text-slate-400 italic">{CAT_TEXTS.kwEmpty}</span>}
        </div>
        <div id="cat-ai-status" role="status" className={gen.errore ? 'text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 mb-2' : (gen.status ? 'text-xs text-slate-500 mb-2' : 'hidden')}>{gen.status}</div>
        <button
          id="cat-ai-generate" type="button" disabled={gen.busy || bloccato}
          className="px-4 py-2 rounded text-sm font-medium text-white"
          style={bloccato ? { background: '#cbd5e1', color: '#334155', cursor: 'not-allowed' } : { background: 'linear-gradient(135deg,#7c3aed,#2563eb)', opacity: gen.busy ? 0.8 : 1, cursor: gen.busy ? 'wait' : 'pointer' }}
          title={bloccato ? "Il servizio AI ha raggiunto il limite d'uso: il pulsante si riattiva da solo all'ora indicata." : undefined}
          onClick={doGenerate}
        >{gen.busy ? <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid rgba(255,255,255,.5)', borderTopColor: '#fff', borderRadius: '50%', verticalAlign: -1, marginRight: 6, animation: 'aiGira .8s linear infinite' }} /> : null}{genLabel}</button>
        {gen.busy ? <style>{'@keyframes aiGira{to{transform:rotate(360deg)}}'}</style> : null}
        <div className="mt-4 pt-3 border-t border-slate-100">
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">{CAT_TEXTS.kwManualLabel}</label>
          <div className="flex gap-2">
            <input
              id="cat-kw-manual-input" type="text"
              placeholder={CAT_TEXTS.kwManualPlaceholder}
              className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm"
              value={manualKw}
              onChange={(e) => setManualKw(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doAddManualKw(); } }}
            />
            <button
              id="cat-kw-manual-add" type="button" disabled={manualBusy}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded text-sm font-medium"
              onClick={doAddManualKw}
            >{CAT_TEXTS.kwManualAdd}</button>
          </div>
          <div className="text-xs text-slate-400 mt-1">{CAT_TEXTS.kwManualHint}</div>
        </div>
      </div>
    </div>
  );
}

function CatDetail({ win, viewState, detailRev, on }) {
  const selected = viewState.selected;
  if (!selected) return <DetailBox>{CAT_TEXTS.noneSelected}</DetailBox>;
  const categories = (win.DB && win.DB.categories) || [];
  const selObj = categories.find((c) => c.path === selected);
  if (!selObj) return <DetailBox>{CAT_TEXTS.notFound}</DetailBox>;

  const isLeaf = win.store.isLeaf(selected);
  const parts = selected.split('/');
  const suppliers = catDetailSuppliers(win, selected);
  const products = win.store.listProducts({ category: selected });
  const quotations = catDetailQuotations(win, products);
  const tab = viewState.tab;
  const counts = { suppliers: suppliers.length, products: products.length, quotations: quotations.length };

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
      <div className="border-b px-5 pt-4 pb-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{CAT_TEXTS.selectedLabel}</div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="font-bold text-lg">
            {parts.map((p, i) => {
              const partial = parts.slice(0, i + 1).join('/');
              return (
                <React.Fragment key={partial}>
                  {i ? <span className="text-slate-300 mx-1">/</span> : null}
                  <button
                    type="button" className="cat-crumb text-slate-500 hover:text-cyan-700"
                    onClick={() => { viewState.selected = partial; catEnsureExpanded(viewState.expanded, partial); on.repaintBoth(); }}
                  >{p}</button>
                </React.Fragment>
              );
            })}
          </div>
          <span className={'text-xs px-2 py-0.5 rounded ' + (isLeaf ? 'bg-cyan-100 text-cyan-800' : 'bg-slate-100 text-slate-700')}>{isLeaf ? CAT_TEXTS.leafBadge : CAT_TEXTS.branchBadge}</span>
        </div>
        <div className="flex gap-1 mt-3 -mb-px">
          {['suppliers', 'products', 'quotations', 'settings'].map((tid) => (
            <button
              key={tid} type="button"
              className={'cat-tab px-3 py-2 text-sm font-medium border-b-2 ' + (tab === tid ? 'border-cyan-700 text-cyan-800' : 'border-transparent text-slate-600 hover:text-slate-900')}
              onClick={() => { viewState.tab = tid; on.repaintDetail(); }}
            >
              {catTabLabel(tid)}
              {tid !== 'settings' ? <>{' '}<span className="ml-1 text-xs bg-slate-100 px-1.5 rounded">{counts[tid]}</span></> : null}
            </button>
          ))}
        </div>
      </div>
      <div className="p-5" id="cat-tab-content">
        {tab === 'suppliers' ? <SuppliersTab win={win} viewState={viewState} suppliers={suppliers} detailRev={detailRev} on={on} /> : null}
        {tab === 'products' ? <ProductsTab win={win} viewState={viewState} products={products} isLeaf={isLeaf} /> : null}
        {tab === 'quotations' ? <QuotationsTab win={win} products={products} quotations={quotations} /> : null}
        {tab === 'settings' ? <SettingsTab win={win} viewState={viewState} selObj={selObj} isLeaf={isLeaf} on={on} /> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VISTA COMPLETA
// ---------------------------------------------------------------------------
export function CategoriesView({ win, tick, viewState }) {
  // repaint granulari: il v1 ridisegna albero e dettaglio SEPARATAMENTE
  // (_renderCatTree/_renderCatDetail) — qui i contatori pilotano gli effect
  // (es. il picker fornitori si ricrea SOLO sui redraw del dettaglio).
  const [, setTreeRev] = React.useState(0);
  const [detailRev, setDetailRev] = React.useState(0);
  const [ctxMenu, setCtxMenu] = React.useState(null);
  // valore GREZZO dell'input (il v1 tiene il trimmed in catSearch ma il DOM
  // conserva il digitato finché la vista non si ridisegna per intero);
  // al render del wrapper (tick) si riallinea al trimmed come il v1.
  const [searchRaw, setSearchRaw] = React.useState(viewState.search || '');
  React.useEffect(() => { setSearchRaw(viewState.search || ''); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const searchRef = React.useRef(null);

  const repaintTree = () => setTreeRev((n) => n + 1);
  const repaintDetail = () => setDetailRev((n) => n + 1);
  const repaintBoth = () => { setTreeRev((n) => n + 1); setDetailRev((n) => n + 1); };

  // Ctrl/Cmd+F → focus sul campo cerca SOLO nella vista Categorie (v1:8134-8143;
  // il v1 lascia il listener globale con la stessa guardia su currentView —
  // qui si rimuove all'unmount: comportamento identico in ogni stato raggiungibile)
  React.useEffect(() => {
    const doc = win.document;
    const hotkey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F')) {
        if (win.currentView !== 'categories') return;
        const el = searchRef.current;
        if (!el) return;
        e.preventDefault(); el.focus(); el.select();
      }
    };
    doc.addEventListener('keydown', hotkey);
    return () => doc.removeEventListener('keydown', hotkey);
  }, [win]);

  const ctx = catTreeContext(win, viewState);
  const roots = win.store.categoryTree();

  // azioni condivise da righe / menu contestuale / dettaglio
  const on = {
    repaintTree, repaintDetail, repaintBoth,
    toggle: (path) => catToggleNode(path, {
      tokens: ctx.tokens,
      effectiveExpanded: ctx.effectiveExpanded,
      manualExpanded: win.catSearchManualExpanded,
      collapsed: win.catSearchCollapsed,
      baseExpanded: viewState.expanded,
    }),
    add: async (path) => { if (await catAdd(win, viewState, path)) repaintBoth(); },
    rename: async (path) => { if (await catRename(win, viewState, path)) repaintBoth(); },
    del: async (path) => { if (await catDelete(win, viewState, path)) repaintBoth(); },
    cut: (path) => { catCut(win, path); },
    paste: async (path) => { if (await catPaste(win, viewState, path)) repaintBoth(); },
    ctxExpandSub: (path) => { catCtxExpandSub(win, viewState, path); repaintTree(); },
    ctxCollapseSub: (path) => { catCtxCollapseSub(win, viewState, path); repaintTree(); },
    ctxOnlyHere: (path) => { catCtxOnlyHere(viewState, path); repaintTree(); },
    openCtxMenu: (x, y, path) => setCtxMenu({ x, y, path }),
    closeCtxMenu: () => setCtxMenu(null),
  };

  const onSearchChange = (e) => {
    const raw = e.target.value;
    setSearchRaw(raw);
    catApplySearchInput(win, viewState, raw);
    repaintTree(); // il v1 ridisegna SOLO l'albero/lista (v1:8156)
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-2xl font-bold">{catLabel(win, 'cat.title')}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{CAT_TEXTS.subtitle}</p>
        </div>
        <div className="flex gap-2">
          <button
            id="cat-add-ai" type="button"
            className="text-white px-3 py-1.5 rounded text-sm"
            style={{ background: 'linear-gradient(135deg,#7c3aed,#2563eb)' }}
            onClick={() => catHeaderAiAssistant(win, viewState)}
          >{CAT_TEXTS.btnAi}</button>
          <button
            id="cat-import-products" type="button"
            className="text-white px-3 py-1.5 rounded text-sm"
            style={{ background: 'linear-gradient(135deg,#059669,#2563eb)' }}
            onClick={() => catHeaderAiImport(win)}
          >{CAT_TEXTS.btnImport}</button>
          <button
            id="cat-add-root" type="button"
            className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded text-sm"
            onClick={() => on.add(null)}
          >{CAT_TEXTS.btnRoot}</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 categories-grid">
        <div className="lg:col-span-6 bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="border-b px-3 py-2 flex items-center gap-2">
            <input
              id="cat-search"
              ref={searchRef}
              placeholder={CAT_TEXTS.searchPlaceholder}
              className="flex-1 border rounded px-2 py-1 text-sm"
              value={searchRaw}
              onChange={onSearchChange}
            />
            <div className="flex border border-slate-200 rounded overflow-hidden text-xs">
              <button
                id="cat-view-tree" type="button"
                className={(viewState.viewMode === 'tree' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50') + ' px-2 py-1'}
                title={CAT_TEXTS.viewTreeTitle}
                onClick={() => { viewState.viewMode = 'tree'; win.renderCategories(); }}
              >{CAT_TEXTS.viewTree}</button>
              <button
                id="cat-view-list" type="button"
                className={(viewState.viewMode === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50') + ' px-2 py-1'}
                title={CAT_TEXTS.viewListTitle}
                onClick={() => { viewState.viewMode = 'list'; win.renderCategories(); }}
              >{CAT_TEXTS.viewList}</button>
            </div>
            <button
              id="cat-expand-all" type="button"
              className="text-xs text-slate-600 hover:text-slate-900 px-1.5" title={CAT_TEXTS.expandAllTitle}
              onClick={() => {
                // _catExpandAll (v1:8085): TUTTI i path (anche foglie, innocuo)
                const categories = (win.DB && win.DB.categories) || [];
                viewState.expanded = new Set(categories.map((c) => c.path));
                repaintTree();
              }}
            >{CAT_TEXTS.expandAll}</button>
            <button
              id="cat-collapse-all" type="button"
              className="text-xs text-slate-600 hover:text-slate-900 px-1.5" title={CAT_TEXTS.collapseAllTitle}
              onClick={() => { viewState.expanded.clear(); repaintTree(); }}
            >{CAT_TEXTS.collapseAll}</button>
          </div>
          <div id="cat-tree-mount" className="p-2 overflow-auto" style={{ maxHeight: '70vh' }}>
            {viewState.viewMode === 'tree' ? (
              <TreeView
                roots={roots}
                nodeKey={(n) => n.path}
                isVisible={ctx.visibleNode}
                isExpanded={(n) => ctx.effectiveExpanded.has(n.path)}
                trailing={(n) => { const hc = ctx.hiddenChildrenCount(n); return hc > 0 ? hc : null; }}
                renderRow={(entry) => <CatRow win={win} entry={entry} ctx={ctx} viewState={viewState} on={on} />}
                renderTrailing={(entry) => (
                  // riga "⋯ altre N categorie" (v1:8371): rivela i fratelli SOLO di quel nodo
                  <div
                    className="cat-row cat-more-row"
                    data-reveal={entry.node.path}
                    style={{ paddingLeft: indentPx(entry.depth) + 'px', color: '#94a3b8', fontStyle: 'italic', cursor: 'pointer' }}
                    onClick={() => { win.catSearchRevealed.add(entry.node.path); repaintTree(); }}
                  >
                    <span className="cat-toggle">⋯</span>
                    <span className="cat-name">{catMoreLabel(entry.data)}</span>
                  </div>
                )}
                emptyMessage={CAT_TEXTS.emptyTree}
              />
            ) : (
              <CatList win={win} viewState={viewState} on={on} />
            )}
          </div>
        </div>
        <div className="lg:col-span-6" id="cat-detail-mount">
          <CatDetail win={win} viewState={viewState} detailRev={detailRev} on={on} />
        </div>
      </div>

      {ctxMenu
        // PORTAL su body: stessa collocazione DOM del menu v1 (v1:8546)
        ? win.ReactDOM.createPortal(<CatContextMenu win={win} menu={ctxMenu} on={on} />, win.document.body)
        : null}
    </div>
  );
}

// Factory (pattern createProcV3Templates) con STATO DI VISTA nella closure:
// selezione/tab/ricerca/espansioni/modo vista sopravvivono a unmount/remount
// (navigazione) e muoiono al reload — stesso ciclo di vita dei binding
// lessicali del v1 (v1:8059-8078).
export function createProcV3Categories(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;
  const viewState = {
    selected: null,      // catSelected v1:8059
    tab: 'suppliers',    // catTab v1:8060
    search: '',          // catSearch v1:8061
    searchPrev: '',      // catSearchPrev v1:8073
    expanded: new Set(), // catExpanded v1:8077
    viewMode: 'tree',    // catViewMode v1:8078
  };

  function render() {
    const container = win.document.getElementById('categories-content');
    if (!container) throw new Error('#categories-content assente');
    // i Set di ricerca globali del v1 (v1:8074-8076) esistono sempre dopo il
    // load di proc-v0.js; guardia difensiva per non esplodere se mancassero
    if (!win.catSearchManualExpanded) win.catSearchManualExpanded = new Set();
    if (!win.catSearchCollapsed) win.catSearchCollapsed = new Set();
    if (!win.catSearchRevealed) win.catSearchRevealed = new Set();
    if (!root || mountedEl !== container || !container.isConnected) {
      if (root) { try { root.unmount(); } catch (e) { /* già smontato */ } }
      container.replaceChildren(); // eventuali resti del v1 (catch-up)
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    // preambolo di renderCategories (v1:8095-8096): SOLO qui, non sui repaint
    // interni (stessa differenza renderCategories vs _renderCatTree del v1)
    catRenderPreamble(win, viewState);
    tick += 1; // ogni chiamata del wrapper = refresh dati (rerenderAll, rientro)
    root.render(<CategoriesView win={win} tick={tick} viewState={viewState} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
